import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  useCertificateStore,
  type CertJobStatus
} from '@stores/certificateStore';
import { openPdfInNewTab } from '@utils/pdfDownload';

const CertificateJobListener = () => {
  const { t } = useTranslation('common');
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const prevStatusRef = useRef<CertJobStatus | null>(null);

  const pendingJob = useCertificateStore(state => state.pendingJob);
  const clearPdfData = useCertificateStore(state => state.clearPdfData);

  const currentStatus = pendingJob?.status ?? null;
  const pdfBase64 = pendingJob?.pdfBase64;
  const errorMessage = pendingJob?.errorMessage;

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = currentStatus;

    if (prevStatus === null || currentStatus === prevStatus) return;

    if (currentStatus === 'waiting') {
      enqueueSnackbar(t('certificate.jobStarted'), {
        variant: 'info',
        autoHideDuration: 5000
      });
    }

    if (currentStatus === 'complete' && pdfBase64) {
      enqueueSnackbar(t('certificate.ready'), {
        variant: 'success',
        persist: true,
        action: snackbarId => (
          <>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                if (pdfBase64) {
                  openPdfInNewTab(pdfBase64);
                }
                clearPdfData();
                closeSnackbar(snackbarId);
              }}
            >
              {t('certificate.viewCertificate')}
            </Button>
            <IconButton
              size="small"
              color="inherit"
              onClick={() => {
                clearPdfData();
                closeSnackbar(snackbarId);
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )
      });
    }

    if (currentStatus === 'complete' && !pdfBase64) {
      enqueueSnackbar(t('certificate.generated'), {
        variant: 'success',
        autoHideDuration: 5000
      });
    }

    if (currentStatus === 'error') {
      enqueueSnackbar(errorMessage ?? t('certificate.generationFailed'), {
        variant: 'error',
        autoHideDuration: 8000
      });
    }
  }, [
    currentStatus,
    pdfBase64,
    errorMessage,
    enqueueSnackbar,
    closeSnackbar,
    clearPdfData,
    t
  ]);

  return null;
};

export default CertificateJobListener;
